import React from 'react'
import {Control, FieldErrors, FieldValues, Path, useController} from 'react-hook-form'
import {getFieldError} from './getFieldError'

type WeeklyRentBudgetInputProps<T extends FieldValues> = {
  minName: Path<T>
  maxName: Path<T>
  control: Control<T>
  errors?: FieldErrors<T>
  label?: string
  subtitle?: string
  className?: string
  min?: number
  max?: number
  step?: number
  defaultValue?: {
    min?: number
    max?: number
  }
}

const DEFAULT_MIN_BUDGET = 50
const DEFAULT_MAX_BUDGET = 5000
const DEFAULT_BUDGET_STEP = 10
const WHEEL_ITEM_HEIGHT = 38
const WHEEL_VISIBLE_ROWS = 5

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

const toFiniteNumber = (value: number | undefined, fallback: number) =>
  value !== undefined && Number.isFinite(value) ? value : fallback

const buildBudgetOptions = (start: number, end: number, step: number) => {
  if (end <= start) {
    return [start]
  }

  const count = Math.floor((end - start) / step) + 1
  const options = Array.from({length: Math.max(count, 1)}, (_, index) =>
    Number((start + index * step).toFixed(6))
  ).filter((value) => value <= end)
  const lastValue = options[options.length - 1]

  if (lastValue !== end) {
    options.push(end)
  }

  return options
}

const buildBudgetRange = (min: number | undefined, max: number | undefined, step: number) => {
  const interval = step > 0 && Number.isFinite(step) ? step : DEFAULT_BUDGET_STEP
  const safeMin = toFiniteNumber(min, DEFAULT_MIN_BUDGET)
  const safeMax = toFiniteNumber(max, DEFAULT_MAX_BUDGET)
  const lower = Math.min(safeMin, safeMax)
  const upper = Math.max(safeMin, safeMax, lower + interval)

  return {
    min: lower,
    max: upper,
    step: interval,
    minOptions: buildBudgetOptions(lower, upper - interval, interval),
    maxOptions: buildBudgetOptions(lower + interval, upper, interval),
  }
}

const normalizeBudgetValue = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const parsed = Number(value)
  if (!Number.isFinite(parsed)) {
    return null
  }

  return parsed
}

const getNearestIndex = (options: number[], value: number) => {
  if (options.length === 0) {
    return 0
  }

  let nearestIndex = 0
  let nearestDistance = Math.abs(options[0] - value)

  for (let index = 1; index < options.length; index += 1) {
    const distance = Math.abs(options[index] - value)

    if (distance < nearestDistance) {
      nearestDistance = distance
      nearestIndex = index
    }
  }

  return nearestIndex
}

const normalizeBudgetValueInOptions = (value: unknown, options: number[]) => {
  const normalized = normalizeBudgetValue(value)

  if (normalized === null || options.length === 0) {
    return null
  }

  return options[getNearestIndex(options, normalized)]
}

const formatBudget = (value: number) => `$${value.toLocaleString('en-AU')}`

const scrollToBudget = (
  container: HTMLDivElement | null,
  options: number[],
  value: number | null,
  behavior: ScrollBehavior
) => {
  if (!container || options.length === 0) {
    return
  }

  const index = getNearestIndex(options, value ?? options[0])

  container.scrollTo({
    top: index * WHEEL_ITEM_HEIGHT,
    behavior,
  })
}

type BudgetWheelProps = {
  value: number | null
  options: number[]
  hasError: boolean
  onSelect: (value: number) => void
  scrollRef: React.RefObject<HTMLDivElement | null>
}

const BudgetWheel = ({value, options, hasError, onSelect, scrollRef}: BudgetWheelProps) => {
  const scrollTimeoutRef = React.useRef<number | null>(null)

  React.useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current !== null) {
        window.clearTimeout(scrollTimeoutRef.current)
      }
    }
  }, [])

  const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (scrollTimeoutRef.current !== null) {
      window.clearTimeout(scrollTimeoutRef.current)
    }

    const container = event.currentTarget
    scrollTimeoutRef.current = window.setTimeout(() => {
      const index = clamp(
        Math.round(container.scrollTop / WHEEL_ITEM_HEIGHT),
        0,
        options.length - 1
      )
      const nextValue = options[index]

      onSelect(nextValue)
      scrollToBudget(container, options, nextValue, 'smooth')
    }, 90)
  }

  return (
    <div className={`wizard-budget-wheel ${hasError ? 'is-invalid' : ''}`.trim()}>
      <div className='wizard-budget-wheel-body'>
        <div className='wizard-budget-wheel-window' aria-hidden='true'></div>
        <div
          ref={scrollRef}
          className='wizard-budget-wheel-scroller'
          onScroll={handleScroll}
          role='listbox'
          aria-label='Weekly rent budget'
        >
          {options.map((option) => (
            <button
              key={option}
              type='button'
              className={`wizard-budget-wheel-option fs-2 ${
                value === option ? 'is-selected' : ''
              }`.trim()}
              onClick={() => {
                onSelect(option)
                scrollToBudget(scrollRef.current, options, option, 'smooth')
              }}
              role='option'
              aria-selected={value === option}
            >
              {formatBudget(option)}
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

export const WeeklyRentBudgetInput = <T extends FieldValues>({
  minName,
  maxName,
  control,
  errors,
  label = 'Weekly rent budget',
  subtitle = 'For the whole household',
  className = '',
  min,
  max,
  step = DEFAULT_BUDGET_STEP,
  defaultValue,
}: WeeklyRentBudgetInputProps<T>) => {
  const budgetRange = React.useMemo(() => buildBudgetRange(min, max, step), [max, min, step])
  const defaultMinValue = React.useMemo(
    () => normalizeBudgetValueInOptions(defaultValue?.min, budgetRange.minOptions),
    [budgetRange.minOptions, defaultValue?.min]
  )
  const defaultMaxValue = React.useMemo(
    () => normalizeBudgetValueInOptions(defaultValue?.max, budgetRange.maxOptions),
    [budgetRange.maxOptions, defaultValue?.max]
  )
  const minField = useController({
    name: minName,
    control,
    ...(defaultMinValue !== null ? {defaultValue: defaultMinValue as T[Path<T>]} : {}),
  })
  const maxField = useController({
    name: maxName,
    control,
    ...(defaultMaxValue !== null ? {defaultValue: defaultMaxValue as T[Path<T>]} : {}),
  })
  const minError = getFieldError(errors, minName)
  const maxError = getFieldError(errors, maxName)
  const minValue = normalizeBudgetValueInOptions(minField.field.value, budgetRange.minOptions)
  const maxValue = normalizeBudgetValueInOptions(maxField.field.value, budgetRange.maxOptions)
  const minScrollRef = React.useRef<HTMLDivElement>(null)
  const maxScrollRef = React.useRef<HTMLDivElement>(null)

  React.useEffect(() => {
    scrollToBudget(minScrollRef.current, budgetRange.minOptions, minValue, 'auto')
  }, [budgetRange.minOptions, minValue])

  React.useEffect(() => {
    scrollToBudget(maxScrollRef.current, budgetRange.maxOptions, maxValue, 'auto')
  }, [budgetRange.maxOptions, maxValue])

  React.useEffect(() => {
    const rawValue = normalizeBudgetValue(minField.field.value)

    if (rawValue === null || minValue === null || rawValue === minValue) {
      return
    }

    minField.field.onChange(minValue)
  }, [minField.field.onChange, minField.field.value, minValue])

  React.useEffect(() => {
    const rawValue = normalizeBudgetValue(maxField.field.value)

    if (rawValue === null || maxValue === null || rawValue === maxValue) {
      return
    }

    maxField.field.onChange(maxValue)
  }, [maxField.field.onChange, maxField.field.value, maxValue])

  React.useEffect(() => {
    if (minValue === null || maxValue === null || minValue < maxValue) {
      return
    }

    maxField.field.onChange(Math.min(minValue + budgetRange.step, budgetRange.max))
  }, [budgetRange.max, budgetRange.step, maxField.field.onChange, maxValue, minValue])

  const handleMinSelect = (nextMin: number) => {
    minField.field.onChange(nextMin)

    if (maxValue !== null && nextMin >= maxValue) {
      maxField.field.onChange(Math.min(nextMin + budgetRange.step, budgetRange.max))
    }
  }

  const handleMaxSelect = (nextMax: number) => {
    maxField.field.onChange(nextMax)

    if (minValue !== null && nextMax <= minValue) {
      minField.field.onChange(Math.max(nextMax - budgetRange.step, budgetRange.min))
    }
  }

  const summaryText = (() => {
    if (minValue !== null && maxValue !== null) {
      return `${formatBudget(minValue)} - ${formatBudget(maxValue)} / week`
    }

    if (minValue !== null) {
      return `From ${formatBudget(minValue)} / week`
    }

    if (maxValue !== null) {
      return `Up to ${formatBudget(maxValue)} / week`
    }

    return 'Select your weekly budget'
  })()

  const hasError = Boolean(minError || maxError)
  const wheelHeight = WHEEL_ITEM_HEIGHT * WHEEL_VISIBLE_ROWS

  return (
    <div className={`wizard-budget-picker ${className}`.trim()}>
      <div className='wizard-budget-picker-heading'>
        <label className='form-label fw-semibold'>
          {label} <span className='text-danger'>*</span>
        </label>
        <span className='wizard-budget-picker-subtitle'>{subtitle}</span>
      </div>

      <div className='wizard-budget-picker-grid mt-5'>
        <div className='wizard-budget-picker-column' style={{height: wheelHeight}}>
          <div className='wizard-budget-picker-column-label'>MIN / WEEK</div>
          <BudgetWheel
            value={minValue}
            options={budgetRange.minOptions}
            hasError={hasError}
            onSelect={handleMinSelect}
            scrollRef={minScrollRef}
          />
        </div>

        <div className='wizard-budget-picker-column' style={{height: wheelHeight}}>
          <div className='wizard-budget-picker-column-label'>MAX / WEEK</div>
          <BudgetWheel
            value={maxValue}
            options={budgetRange.maxOptions}
            hasError={hasError}
            onSelect={handleMaxSelect}
            scrollRef={maxScrollRef}
          />
        </div>
      </div>

      <div className='wizard-budget-picker-summary'>{summaryText}</div>

      {minError && <div className='text-danger mt-1'>{minError.message}</div>}
      {maxError && maxError.message !== minError?.message && (
        <div className='text-danger mt-1'>{maxError.message}</div>
      )}
    </div>
  )
}
